import { useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons';
import Alert from '@v2/shared/components/Alert';
import Button from '@v2/shared/components/Button';
import { FormInput, FormSelect, FormTextArea } from '@v2/shared/components/Form';
import FormField from '@v2/shared/components/Form/FormField';
import { controlClasses } from '@v2/shared/components/Form/controlClasses';
import { cn } from '@v2/shared/lib/utils';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import useContactForm, { FORM_TYPE } from './useContactForm';
import {
  FIELD_MESSAGES,
  ORDERS_URL,
  ORDER_NUMBER_PATTERN,
  REASON_GROUPS,
} from './constants';
import type { ContactSubject } from './types';
import { liveAgentHours } from '@v2/shared/lib/supportHours';

const HELP = 'mt-1 text-sm text-slate-500';

type Props = {
  subjects: ContactSubject[];
  /**
   * The form's state, owned by the page rather than by this component.
   *
   * The topic cards sit several screens above the form and each of them can select a
   * reason in it, so the state has to live somewhere both can see. The page holds it and
   * hands it down; this component is the half of it that is a form.
   */
  form: ReturnType<typeof useContactForm>;
};

/**
 * The contact form, posting into the same `contact.php?xCmd=send` branch the page it
 * replaces posted into -- over fetch when it can, so a failure can be shown in place
 * instead of reloading the page.
 *
 * It keeps a real `action` and `method` all the same, and the legacy names on its inputs,
 * because the page is server-rendered: the form is on screen and submittable before the
 * bundle has hydrated, and it is the whole page for a visitor with scripting off.
 * Without them the browser's own submit is a GET to this url -- the message is never
 * sent, nothing says so, and the whole of it ends up in the address bar, the access log
 * and the referrer.
 */
export default function ContactForm({ subjects, form }: Props) {
  const { buildUrl } = useUrlGenerator();
  const {
    register,
    handleSubmit,
    errors,
    onSubmit,
    action,
    submitState,
    submitMessage,
    isSubmitting,
    defaultSubject,
    findOrderRef,
    control,
    hasAskedForOrder,
  } = form;

  /**
   * A soft warning, not a rule: the field is optional and a submission is never blocked
   * on it. It catches the account number off the magazine's own label, which is the
   * number people reach for and the one number nothing can be looked up with.
   */
  const [orderLooksWrong, setOrderLooksWrong] = useState(false);

  // Watched here rather than in the hook, which the page owns: these subscriptions
  // re-render this component and not the four topic cards above it.
  const subject = useWatch({ control, name: 'Subject' });
  const [order, recipientName, recipientZip] = useWatch({
    control,
    name: ['order', 'recipientName', 'recipientZip'],
  });

  /**
   * The prompt asks once, and stops the moment the question is answered.
   *
   * `order` counts, not just the two fields inside the fieldset. The question is "how do
   * we find your order", and an order number answers it better than a name and a ZIP do
   * -- so someone who responds to the prompt by going back and filling the order field
   * would otherwise be left looking at an amber box still asking them for something they
   * have just supplied.
   */
  const isAskingForOrder =
    hasAskedForOrder && !order.trim() && !recipientName.trim() && !recipientZip.trim();

  // Stable across the keystrokes above: `subjects` is a server prop and REASON_GROUPS a
  // module constant, so the dropdown is built once rather than on every character.
  const offeredGroups = useMemo(() => {
    const accepted = new Set(subjects.map((entry) => entry.id));

    return REASON_GROUPS.map((group) => ({
      label: group.label,
      reasons: group.reasons.filter((reason) => accepted.has(reason.id)),
    })).filter((group) => group.reasons.length > 0);
  }, [subjects]);

  if (submitState === 'success') {
    return (
      <Alert color="success" className="mb-0">
        <p className="m-0">
          <b>Message sent.</b> Thank you. We reply by email, so watch your inbox (and your spam
          folder) for a message from DiscountMags. Please send one message per issue; a second
          message about the same thing doesn&apos;t move it up the queue.
        </p>
      </Alert>
    );
  }

  const titleHelp =
    subject === '26'
      ? 'List every magazine you want to stop renewing.'
      : 'Exactly as printed on the label. If you have several, list the ones this is about.';

  return (
    <form
      action={action}
      method="post"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="grid grid-cols-1 items-start gap-7 lg:grid-cols-[minmax(0,1fr)_260px]"
    >
      <input type="hidden" name="formType" value={FORM_TYPE} />

      <div>
        {submitState === 'error' && submitMessage && <Alert color="danger">{submitMessage}</Alert>}

        <FormSelect
          id="Subject"
          label="What is this about?"
          className="w-full min-w-0"
          defaultValue={defaultSubject}
          disabled={isSubmitting}
          error={errors.Subject?.message}
          {...register('Subject', { required: FIELD_MESSAGES.Subject })}
        >
          <option value="">Choose a topic</option>
          {offeredGroups.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.reasons.map((reason) => (
                <option key={reason.id} value={reason.id}>
                  {reason.label}
                </option>
              ))}
            </optgroup>
          ))}
        </FormSelect>

        <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
          <FormInput
            id="name"
            label="Your name"
            type="text"
            autoComplete="name"
            placeholder="First and last name"
            maxLength={100}
            disabled={isSubmitting}
            error={errors.name?.message}
            {...register('name', { required: FIELD_MESSAGES.name })}
          />

          <FormInput
            id="EmailAddress"
            label="Email"
            type="email"
            autoComplete="email"
            placeholder="Where we should reply"
            maxLength={250}
            disabled={isSubmitting}
            error={errors.EmailAddress?.message}
            {...register('EmailAddress', {
              required: FIELD_MESSAGES.EmailAddress,
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: FIELD_MESSAGES.EmailAddress,
              },
            })}
          />
        </div>

        <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
          <FormField id="order" label="Order number (if you have it)">
            <input
              id="order"
              type="text"
              autoComplete="off"
              placeholder="e.g. AB1234567"
              maxLength={20}
              disabled={isSubmitting}
              className={controlClasses(false)}
              {...register('order', {
                // Read off the event rather than through getValues: the blur already
                // carries the value, and react-hook-form still runs its own handler
                // either way -- these options wrap it, they do not replace it.
                onBlur: (event) => {
                  const entered = event.target.value.replace(/\s+/g, '').toUpperCase();
                  setOrderLooksWrong(entered !== '' && !ORDER_NUMBER_PATTERN.test(entered));
                },
                onChange: () => setOrderLooksWrong(false),
              })}
            />
            {orderLooksWrong && (
              <span className="mt-1 text-sm text-amber-800">
                Order numbers are two letters and seven digits, like AB1234567. Check the number or
                leave it blank.
              </span>
            )}
            <span className={HELP}>
              If it has renewed, use the number from your most recent charge. Not the account number
              on your label.{' '}
              <a className="text-primary" href={buildUrl('/help/find-order-number')}>
                Where to find it
              </a>
            </span>
          </FormField>

          <FormField id="magazines" label="Title (optional)">
            <input
              id="magazines"
              type="text"
              autoComplete="off"
              placeholder="Magazine or book, as on the label"
              maxLength={250}
              disabled={isSubmitting}
              className={controlClasses(false)}
              {...register('magazines')}
            />
            <span className={HELP}>{titleHelp}</span>
          </FormField>
        </div>

        {/* Optional, and it stays optional: the prompt below interrupts one submit and
            never a second. A message we cannot place is still a message, and refusing it
            would lose the ones from exactly the people who cannot find their order. */}
        <fieldset
          ref={findOrderRef}
          className={cn(
            'mb-5 mt-0 rounded-lg border-2 border-solid px-4 pb-0 pt-3',
            isAskingForOrder ? 'border-amber-500 bg-amber-50' : 'border-slate-200 bg-slate-50'
          )}
        >
          <legend className="px-1.5 font-semibold text-slate-900">
            Can&apos;t find your order? (optional)
          </legend>

          <p className={cn('mb-3 mt-0 text-sm', isAskingForOrder ? 'text-amber-800' : 'text-slate-500')}>
            {isAskingForOrder
              ? 'Add these so we can find your order, or press Send Message again to send without them.'
              : 'No order number? Add the name and ZIP on the subscription so we can find it without writing back to you.'}
          </p>

          <div className="grid grid-cols-1 gap-x-4 md:grid-cols-[2fr_1fr]">
            <FormInput
              id="recipientName"
              label="Name on the subscription"
              type="text"
              autoComplete="off"
              placeholder="As shown on the mailing label"
              maxLength={100}
              disabled={isSubmitting}
              {...register('recipientName')}
            />

            <FormInput
              id="recipientZip"
              label="ZIP code"
              type="text"
              autoComplete="off"
              inputMode="numeric"
              placeholder="e.g. 85255"
              maxLength={20}
              disabled={isSubmitting}
              {...register('recipientZip')}
            />
          </div>
        </fieldset>

        <FormTextArea
          id="cfComments"
          label="Your message"
          rows={5}
          maxLength={10240}
          placeholder="Tell us what happened and what you'd like us to do. For a charge, include the amount and date."
          disabled={isSubmitting}
          error={errors.cfComments?.message}
          {...register('cfComments', { required: FIELD_MESSAGES.cfComments })}
        />

        <Button
          type="submit"
          isLoading={isSubmitting}
          loadingText="Sending…"
          shape="rounded"
          icon={faPaperPlane}
        >
          Send Message
        </Button>

        <p className="mb-0 mt-4 text-sm text-slate-500">
          We reply by email from a discountmags.com address. If you don&apos;t see it, check your
          spam folder. <b className="text-slate-900">Automated order support:</b> 24/7 &nbsp;·&nbsp;{' '}
          <b className="text-slate-900">Live agents:</b> {liveAgentHours()}
        </p>
      </div>

      <aside className="rounded-lg border border-solid border-slate-200 bg-slate-50 px-5 py-4 text-sm text-slate-700">
        <h3 className="mb-2 mt-0 text-base font-extrabold text-slate-900">What to include</h3>
        <ul className="m-0 mb-4 list-disc pl-5 marker:text-primary">
          <li className="mb-1.5">Your order number, or the name and ZIP on the subscription.</li>
          <li className="mb-1.5">For a charge: the amount and the date it appeared.</li>
          <li>For a missing issue: the last issue you received.</li>
        </ul>
        <p className="m-0 border-0 border-t border-solid border-slate-200 pt-3 text-slate-600">
          <b className="text-slate-900">Signed in?</b> Open the order in{' '}
          <a className="text-primary" href={buildUrl(ORDERS_URL)}>
            My Orders
          </a>{' '}
          and choose &quot;Get help with this order&quot;. Your details and the order come attached,
          so there&apos;s nothing to type.
        </p>
      </aside>
    </form>
  );
}
